/**
 * AppointmentMedicalRequestField.js
 * Bloco "Pedido médico" do drawer de novo agendamento por convênio: escolhe um
 * pedido ativo da ficha (já vem o válido emitido mais recente) ou registra um novo
 * pelo mesmo formulário da ficha, que fica selecionado ao salvar. Quem vincula
 * o pedido ao agendamento é o drawer, depois de criar (linkRequestToAppointments).
 * Os documentos e as operadoras vêm do drawer (usePatientInsuranceDocuments),
 * buscados uma vez para este bloco e o da guia.
 *
 * Aparece em todo agendamento por convênio; o asterisco só vem quando a operadora
 * exige pedido. Nunca bloqueia: sem pedido o agendamento salva e o aviso de
 * "Convênios a conferir" continua valendo.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import MedicalRequestFormPopup from '../../../../patient/areas/insurance/documents/MedicalRequestFormPopup';
import { formatDate } from '../../../../patient/areas/insurance/documents/documentFormat';
import { toDayKey } from '../../../../../components/day-rooms/dayRooms';
import drawerStyles from './AppointmentDrawer.module.css';
import styles from './CoveragePicker.module.css';

const dateOnly = (value) => (value ? String(value).slice(0, 10) : '');

/** Pedido sem vencimento vale sempre; com vencimento, até o dia dele. */
export const isRequestValidOn = (request, dayKey) => (
  !request?.valid_until || !dayKey || dateOnly(request.valid_until) >= dayKey
);

const isActive = (request) => request.active !== 0 && request.active !== false && request.active !== '0';

const issuedKey = (request) => dateOnly(request.issued_at || request.created_at);

/**
 * O pedido não tem operadora (vale para qualquer carteirinha): só ordena.
 * Válidos na data primeiro, emitido mais recente no topo; vencidos por último.
 */
export function sortRequestsForDay(requests, dayKey) {
  return [...(requests || [])].sort((a, b) => {
    const validDiff = Number(isRequestValidOn(b, dayKey)) - Number(isRequestValidOn(a, dayKey));
    if (validDiff !== 0) return validDiff;
    const issuedDiff = issuedKey(b).localeCompare(issuedKey(a));
    return issuedDiff !== 0 ? issuedDiff : Number(b.id) - Number(a.id);
  });
}

/**
 * Vincula o documento (pedido por padrão, ou 'authorization' para a guia) aos
 * agendamentos, em sequência (o endpoint é um por vez).
 * Resolve com quantos falharam; nunca rejeita.
 */
export function linkRequestToAppointments(link, appointmentIds, documentId, documentType = 'medical_request') {
  const ids = (appointmentIds || []).filter((id) => id != null);
  if (!documentId || ids.length === 0) return Promise.resolve(0);
  return ids.reduce((chain, id) => chain.then((failed) => new Promise((resolve) => {
    link(id, documentType, documentId, (result) => {
      resolve(failed + (result?.status === 'SUCCESS' ? 0 : 1));
    });
  })), Promise.resolve(0));
}

/**
 * @param {Object} props
 * @param {number|string} props.patientId
 * @param {string|null} props.providerId - operadora da carteirinha escolhida
 * @param {Object} props.insuranceDocuments - retorno de usePatientInsuranceDocuments
 * @param {Date} props.appointmentDate - data do atendimento (a 1ª, na recorrência)
 * @param {Object|null} props.value - pedido selecionado ({ id, title, valid_until, ... })
 * @param {Function} props.onChange - (pedido | null) => void
 * @param {boolean} [props.isRecurring] - texto de ajuda da recorrência
 * @param {boolean} [props.disabled]
 */
export default function AppointmentMedicalRequestField({
  patientId, providerId, insuranceDocuments, appointmentDate, value, onChange, isRecurring = false, disabled = false,
}) {
  const { SetMedicalRequest } = DataProviderInstance();
  const { documents, providerRules, reload, addDocument } = insuranceDocuments;
  const [formOpen, setFormOpen] = useState(false);
  const dayKey = appointmentDate ? toDayKey(appointmentDate) : '';

  // Pedidos ativos da ficha (null = carregando)
  const requests = useMemo(() => (documents ? documents.requests.filter(isActive) : null), [documents]);

  // Operadoras que exigem pedido (o CompanyConfig não traz a exigência)
  const requiredProviders = useMemo(
    () => providerRules.filter((p) => Number(p.requires_medical_request) === 1).map((p) => String(p.id)),
    [providerRules],
  );

  // Na primeira carga de cada pessoa, o válido mais recente já vem escolhido
  const preselectedFor = useRef(null);
  useEffect(() => {
    if (!requests || patientId == null || preselectedFor.current === patientId) return;
    preselectedFor.current = patientId;
    const [newest] = sortRequestsForDay(requests, dayKey);
    if (newest && isRequestValidOn(newest, dayKey) && !value) onChange?.(newest);
  }, [requests, patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const required = providerId != null && requiredProviders.includes(String(providerId));
  const selected = useMemo(
    () => (value ? (requests || []).find((r) => String(r.id) === String(value.id)) || value : null),
    [requests, value],
  );
  const selectedExpired = selected && !isRequestValidOn(selected, dayKey);

  const handleSelect = (id) => {
    onChange?.(id ? (requests || []).find((r) => String(r.id) === String(id)) || null : null);
  };

  // Grava como a ficha e devolve { ok, id } ao formulário; o pedido novo fica escolhido
  const saveRequest = (values) => new Promise((resolve) => {
    SetMedicalRequest({ patientId, ...values }, (result) => {
      const id = result?.status === 'SUCCESS' ? result.data?.id : null;
      if (!id) {
        resolve({ ok: false, error: result?.error || result?.message || 'Não foi possível salvar o pedido. Tente de novo.' });
        return;
      }
      const created = { id, active: 1, ...values };
      addDocument('medical_request', created);
      onChange?.(created);
      // A recarga (contadores e "vencido" do backend) sai no onSaved, depois dos anexos
      resolve({ ok: true, id });
    });
  });

  const hasRequests = (requests || []).length > 0;
  const sortedRequests = useMemo(() => sortRequestsForDay(requests, dayKey), [requests, dayKey]);

  let status = null;
  if (selected) {
    if (selectedExpired) {
      status = (
        <div className={styles.warning}>
          <span className={styles.warningBadge}>Vencido</span>
          <span>
            Venceu em {formatDate(dateOnly(selected.valid_until))}, antes da data deste atendimento.
            Registre um pedido novo.
          </span>
        </div>
      );
    } else {
      status = (
        <span className={styles.hint}>
          {selected.valid_until ? `Vence em ${formatDate(dateOnly(selected.valid_until))}` : 'Sem vencimento'}
          {isRecurring && selected.valid_until ? '. Fica nas datas até o vencimento.' : ''}
        </span>
      );
    }
  } else if (requests !== null) {
    status = (
      <span className={styles.hint}>
        {hasRequests ? '' : 'Nenhum pedido na ficha. '}
        {required
          ? 'A operadora exige pedido; sem ele o agendamento é salvo e fica em Convênios a conferir.'
          : 'Opcional para esta operadora.'}
      </span>
    );
  }

  return (
    <div className={drawerStyles.formSection}>
      <div className={styles.headerRow}>
        <label
          className={`${drawerStyles.fieldLabel} ${required ? drawerStyles.fieldLabelRequired : ''}`}
          htmlFor="appointment-medical-request"
        >
          Pedido médico
        </label>
        <button type="button" className={styles.linkBtn} onClick={() => setFormOpen(true)} disabled={disabled}>
          Registrar pedido
        </button>
      </div>
      {requests === null ? (
        <span className={styles.loading}>Carregando pedidos...</span>
      ) : hasRequests && (
        <select
          id="appointment-medical-request"
          className={drawerStyles.fieldSelect}
          value={selected ? String(selected.id) : ''}
          onChange={(e) => handleSelect(e.target.value)}
          disabled={disabled}
        >
          <option value="">Sem pedido</option>
          {sortedRequests.map((r) => {
            let validity = ' (vencido)';
            if (isRequestValidOn(r, dayKey)) {
              validity = r.valid_until ? ` - vence ${formatDate(dateOnly(r.valid_until))}` : ' - sem vencimento';
            }
            return (
              <option key={r.id} value={String(r.id)}>
                {r.title || 'Pedido médico'}
                {r.issued_at ? ` - emitido em ${formatDate(dateOnly(r.issued_at))}` : ''}
                {validity}
              </option>
            );
          })}
        </select>
      )}
      {status}

      {formOpen && (
        <MedicalRequestFormPopup
          patientId={patientId}
          onSave={saveRequest}
          onSaved={() => reload()}
          onClose={() => setFormOpen(false)}
        />
      )}
    </div>
  );
}
